import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { IoMdClose } from "react-icons/io";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import profileClasses from "../profileDetailsModal/profile.module.scss";
import consentClasses from "./consent.module.scss";
import { Get_Parent_Consent, Update_Parent_Consent } from "Service/parent";
import { SuccessSwal, warningSwal } from "Reusable/utils/Toast";

type ConsentItem = {
    ConsentID: number;
    ConsentType: string;
    ConsentText: string;
    IsRequired: number;
    IsChecked: number;
};

type props = {
    // Opened manually from the profile menu ("Manage Consent")
    showModal: boolean;
    setModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const Consent: React.FC<props> = ({ showModal, setModal }) => {
    const Navigate = useNavigate();
    const [, , removeCookie] = useCookies([
        "parentDetails",
        "StudentDetails",
        "HomepageCourses",
        "HomepageData",
        "currentStudent",
    ]);
    const [consents, setConsents] = useState<ConsentItem[]>([]);
    // ConsentID -> ticked (local edits)
    const [checked, setChecked] = useState<Record<number, boolean>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showError, setShowError] = useState(false);

    const token = {
        Authorization: `Bearer ${localStorage.getItem("learn2readToken")}`,
    };

    // A required consent that the server has not recorded as checked
    // blocks the parent from using the app until it is given.
    const mustConsent = consents.some(
        (item) => item.IsRequired === 1 && item.IsChecked !== 1
    );

    const fetchConsent = async () => {
        setLoading(true);
        try {
            const response = await Get_Parent_Consent("GET", null, token);
            if (response?.Status === 1) {
                const data: ConsentItem[] = response.Data || [];
                setConsents(data);
                setChecked(
                    Object.fromEntries(
                        data.map((item) => [item.ConsentID, item.IsChecked === 1])
                    )
                );
            } else {
                setConsents([]);
            }
        } catch (err) {
            console.log(err);
            setConsents([]);
        } finally {
            setLoading(false);
        }
    };

    // Consent belongs to the parent, so one check per session load is enough
    useEffect(() => {
        fetchConsent();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        // Reset any unsaved edits when the manual modal is reopened
        if (showModal) {
            setShowError(false);
            fetchConsent();
        }
        // eslint-disable-next-line
    }, [showModal]);

    const closeModal = () => {
        if (mustConsent) return;
        setShowError(false);
        setModal(false);
    };

    const saveHandler = async () => {
        const missingRequired = consents.some(
            (item) => item.IsRequired === 1 && !checked[item.ConsentID]
        );
        if (missingRequired) {
            setShowError(true);
            return;
        }
        // The API only takes consents whose value changed
        const changed = consents
            .filter((item) => !!checked[item.ConsentID] !== (item.IsChecked === 1))
            .map((item) => ({
                ConsentID: item.ConsentID,
                IsChecked: !!checked[item.ConsentID],
            }));
        if (changed.length === 0) {
            setModal(false);
            return;
        }
        setSaving(true);
        try {
            const body = { consent: changed };
            const response = await Update_Parent_Consent(
                "POST",
                JSON.stringify(body),
                token
            );
            if (response?.Status === 1) {
                await fetchConsent();
                setModal(false);
                SuccessSwal("Saved", "Your consent preferences were updated");
            } else {
                warningSwal(
                    "Warning",
                    response?.Message || "Unable to save consent"
                );
            }
        } catch (err) {
            console.log(err);
            warningSwal("Warning", "Something went wrong try again");
        } finally {
            setSaving(false);
        }
    };

    const logoutHandler = () => {
        localStorage.clear();
        sessionStorage.clear();
        removeCookie("currentStudent");
        removeCookie("HomepageData");
        removeCookie("StudentDetails");
        removeCookie("parentDetails");
        removeCookie("HomepageCourses");
        Navigate("/");
    };

    const isOpen = mustConsent || (showModal && consents.length > 0);

    return (
        <Modal
            isOpen={isOpen}
            contentLabel="Manage Consent"
            className={profileClasses["modal"]}
            onRequestClose={closeModal}
            shouldCloseOnOverlayClick={!mustConsent}
            shouldCloseOnEsc={!mustConsent}
            overlayClassName={consentClasses["overlay"]}>
            <div className={profileClasses["Container"]}>
                <header>
                    <h3>Manage Consent</h3>
                    {!mustConsent && (
                        <button onClick={closeModal}>
                            <IoMdClose />
                        </button>
                    )}
                </header>
                <div className={consentClasses["consent"]}>
                    {mustConsent && (
                        <p className={consentClasses["notice"]}>
                            Please accept the required consent to continue
                            using Learn2Read.
                        </p>
                    )}
                    {consents.map((item) => {
                        // Once a required consent is given it can't be withdrawn here
                        const locked =
                            item.IsRequired === 1 && item.IsChecked === 1;
                        return (
                            <label
                                key={item.ConsentID}
                                className={
                                    locked ? consentClasses["locked"] : ""
                                }>
                                <input
                                    type="checkbox"
                                    checked={!!checked[item.ConsentID]}
                                    disabled={locked || saving}
                                    onChange={(e) => {
                                        const value = e.target.checked;
                                        setChecked((prev) => ({
                                            ...prev,
                                            [item.ConsentID]: value,
                                        }));
                                        setShowError(false);
                                    }}
                                />
                                <span>
                                    {item.ConsentText}
                                    <em>
                                        {item.IsRequired
                                            ? " (Required)"
                                            : " (Optional)"}
                                    </em>
                                </span>
                            </label>
                        );
                    })}
                    {showError && (
                        <p className={consentClasses["error-msg"]}>
                            Please tick the required checkbox to continue
                        </p>
                    )}
                </div>
                <div
                    className={profileClasses["Action-btns"]}
                    style={{ paddingBottom: "1rem" }}>
                    {mustConsent && (
                        <button
                            type="button"
                            className={consentClasses["signout"]}
                            onClick={logoutHandler}>
                            Sign Out
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={saveHandler}
                        disabled={saving || loading}>
                        {!saving ? "Save" : "...Please wait"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default Consent;
